import { apiSlice } from '../api/apiSlice';
import {
  addOrUpdateChatroom,
  initChatroomMessages,
  pushChatroomMessages,
  setAnnouncements,
  setChatrooms,
  setMatchChat,
} from './inboxSlice';

export const inboxApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    initChatroomMessages: builder.query({
      query: ({ id }) => ({
        url: `inbox/chatroom/get/${id}`,
        method: 'GET',
      }),
      providesTags: ['SingleChatroom'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(addOrUpdateChatroom(data));
          dispatch(
            apiSlice.util.invalidateTags([
              { type: 'Chatroom', id: 'ALL' },
              { type: 'Chatroom', id: arg.id },
            ]),
          );
        } catch (error) {
          console.error('Query failed:', error);
        }
      },
    }),
    createChatroom: builder.mutation({
      query: ({ body }) => ({
        url: `inbox/chatroom/new`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Chatroom', id: 'ALL' }],
    }),
    updateLastReadDate: builder.mutation({
      query: ({ chatroomId }) => ({
        url: `inbox/chatroom/update-last-read-date/${chatroomId}`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { chatroomId }) => [
        { type: 'Chatroom', id: chatroomId },
        { type: 'Chatroom', id: 'ALL' },
      ],
    }),
    getAllChatrooms: builder.query({
      query: () => ({
        url: `inbox/chatroom/all`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }: { id: string }) => ({ type: 'Chatroom' as const, id })),
              { type: 'Chatroom', id: 'ALL' },
            ]
          : [{ type: 'Chatroom', id: 'ALL' }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setChatrooms(data));
        } catch (error) {
          console.error('Query failed:', error);
        }
      },
    }),
    createChatroomDispute: builder.mutation({
      query: ({ id, body }) => ({
        url: `inbox/chatroom/dispute/${id}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MatchChatDispute'],
    }),
    resolveChatroomDispute: builder.mutation({
      query: ({
        chatroomId,
        body,
      }: {
        chatroomId: string;
        body: { disputeId: number; resolveAll: boolean };
      }) => ({
        url: `/inbox/resolve-chatroom-dispute/${chatroomId}`,
        method: 'PUT',
        body,
      }),
    }),
    getAllAnnouncements: builder.query({
      query: (query) => ({
        url: `inbox/announcements/all`,
        method: 'GET',
        params: query.params ?? {
          [query.query]: query.param,
          page: query.page ?? 1,
        },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setAnnouncements(data));
        } catch (error) {
          console.error('Query failed:', error);
        }
      },
      providesTags: ['InboxAnnouncement'],
    }),
    getAllMatchChats: builder.query({
      query: () => ({
        url: `inbox/match-chat/all`,
        method: 'GET',
      }),
      providesTags: ['MatchChat'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setMatchChat(data));
        } catch (error) {
          console.error('Query failed:', error);
        }
      },
    }),
    getAllFlaggedMatchChats: builder.query({
      query: () => ({
        url: `inbox/match-chat/all?flaggedOnly=true`,
        method: 'GET',
      }),
      providesTags: ['MatchChat'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const state = dispatch((_, getState) => getState());
          if (!state.inbox.matchChats || state.inbox.matchChats.length === 0) {
            dispatch(setMatchChat(data));
          }
        } catch (error) {
          console.error('Query failed:', error);
        }
      },
    }),
    markAnnouncementAsReadByMatchId: builder.mutation({
      query: ({ id }: { id: number }) => ({
        url: `inbox/announcement/mark-read/${id}`,
        method: 'POST',
      }),
      invalidatesTags: ['InboxAnnouncement'],
    }),
    markMatchChatsAsReadByMatchId: builder.mutation({
      query: ({ id }: { id: number }) => ({
        url: `inbox/match-chat/mark-as-read/${id}`,
        method: 'POST',
      }),
      invalidatesTags: ['MatchChat'],
    }),
    archiveAnnouncements: builder.mutation({
      query: ({ body }: { body: { announcementIds: number[] } }) => ({
        url: `inbox/announcements/archive`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['InboxAnnouncement'],
    }),
    archiveChatrooms: builder.mutation({
      query: ({ body }: { body: { chatroomIds: number[] } }) => ({
        url: `inbox/chatroom/archive`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Chatroom', id: 'ALL' }],
    }),
    archiveMatchChats: builder.mutation({
      query: ({ body }: { body: { matchChatIds: number[] } }) => ({
        url: `inbox/match-chats/archive`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['MatchChat'],
    }),
  }),
});

export const {
  useInitChatroomMessagesQuery,
  useCreateChatroomMutation,
  useUpdateLastReadDateMutation,
  useGetAllChatroomsQuery,
  useCreateChatroomDisputeMutation,
  useResolveChatroomDisputeMutation,
  useGetAllAnnouncementsQuery,
  useLazyGetAllAnnouncementsQuery,
  useGetAllMatchChatsQuery,
  useGetAllFlaggedMatchChatsQuery,
  useMarkAnnouncementAsReadByMatchIdMutation,
  useMarkMatchChatsAsReadByMatchIdMutation,
  useArchiveAnnouncementsMutation,
  useArchiveChatroomsMutation,
  useArchiveMatchChatsMutation,
} = inboxApiSlice;
